import { Button } from '@sunwebgroup/uds-ui/button'
import { RadioButtonField } from '@sunwebgroup/uds-ui/fields/radio-button-field'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { isEmpty } from 'ramda'
import { MouseEvent, use, useCallback, useEffect, useState } from 'react'
import { useIntl } from '@/intl'
import { RoomDetails } from '@/scopes/common/components/RoomDetails/RoomDetails'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { roomSelector } from '@/scopes/prices/components/selectors'
import { createRoomSelectorInteractionEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import styles from './Room.module.css'

interface RoomProps {
  id: string
  roomNumber: string
}

function Room({ id, roomNumber }: RoomProps) {
  const intl = useIntl()

  const { selectRoom } = use(PricesActionsContext)

  const setSelectedRoom = (roomId: string | null) => {
    selectRoom(roomNumber, roomId)
  }
  const { room, stock, apiStock, isSelected } = usePricesSelector(roomSelector, roomNumber, id)
  const { name, subtitle, price, characteristics, images, onRequest } = room ?? {}
  const showMoreButton = !isEmpty(characteristics) || !isEmpty(images)
  const { sendEvent } = useAnalytics()

  const [isLocalSelected, setIsLocalSelected] = useState(isSelected)
  const [stockText, setStockText] = useState('')

  const getStockText = useCallback((): { text: string, color: 'info' | 'discount' } => {
    const stockForText = isSelected ? apiStock : stock

    if (stockForText === undefined) {
      return { text: '', color: 'info' }
    }

    if (stockForText === 1) {
      return {
        text: intl.formatMessage({
          id: 'prices_room_selector_room-stock-last-room',
        }),
        color: 'discount',
      }
    }

    return {
      text: intl.formatMessage(
        {
          id: 'prices_room_selector_room-stock-last-rooms-left',
        },
        {
          stock: stockForText,
        },
      ),
      color: 'info',
    }
  }, [isSelected, intl, apiStock, stock])

  const onChange = (event: MouseEvent<HTMLButtonElement>) => {
    setIsLocalSelected(event.currentTarget.value === id)
    setSelectedRoom(event.currentTarget.value)
  }

  useEffect(() => {
    setIsLocalSelected(isSelected)
  }, [isSelected])

  useEffect(() => {
    if (stock <= 5 || apiStock === undefined) {
      const stockObject = getStockText()
      setStockText(stockObject.text)
    }
  }, [isSelected, stock, apiStock, getStockText])

  const [isOpen, setIsOpen] = useState(false)

  if (!room) {
    return null
  }

  const handleSheetOpen = (isOpen: boolean) => {
    setIsOpen(isOpen)
    sendEvent(createRoomSelectorInteractionEvent({ type: name!, result: 'Show more' }))
  }

  const handleImageClick = (index: number) => {
    sendEvent(createPDPClickEvent({
      element: 'Slider',
      description: 'Roomtype images',
      result: `Open image ${index + 1}`,
      type: name,
    }))
  }

  const handleGallerySettle = () => {
    sendEvent(createPDPClickEvent({
      element: 'Slider',
      description: 'Roomtype image Gallery',
      result: 'Slide',
      type: name,
    }))
  }

  const priceText = intl.formatTruncatedCurrency(price!)

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- RadioButtonField renders the nested radio control.
    <label className={styles.room} aria-label={name}>
      <ListItem variant="outlined" hasHover={isLocalSelected}>
        <div className={styles.header}>
          <div className={styles.control}>
            <RadioButtonField value={id} checked={isLocalSelected} onClick={onChange} aria-label={name} />
          </div>
          <div className={styles.content}>
            <div className={styles.attributes}>
              {stockText && <Tag variant="neutral-tinted">{stockText}</Tag>}
              {onRequest && (
                <Tag variant="neutral-tinted">
                  {intl.formatMessage({ id: 'prices_room_selector_room-stock-on-request-room' })}
                </Tag>
              )}
            </div>
            <Typography variant="body-m-bold-snug">{name}</Typography>
            <Typography className={styles.description} variant="body-s-regular-snug">{subtitle}</Typography>
            <Sheet.Root open={isOpen} onOpenChange={handleSheetOpen}>
              {showMoreButton && (
                <Sheet.Trigger>
                  <Button className={styles.showMoreButton} variant="transparentNeutral">
                    {intl.formatMessage({ id: 'prices_room_selector_show_room_details' })}
                  </Button>
                </Sheet.Trigger>
              )}
              <Sheet.Body side="right">
                <Sheet.Header
                  titleLabel={intl.formatMessage({ id: 'prices_room_selector_description_heading' })}
                  closeLabel={intl.formatMessage({ id: 'close' })}
                />
                <Sheet.Content className={styles.sheetContent}>
                  <RoomDetails
                    name={room.name}
                    images={images || []}
                    characteristics={characteristics || []}
                    onImageClick={handleImageClick}
                    onGallerySettle={handleGallerySettle}
                  />
                </Sheet.Content>
              </Sheet.Body>
            </Sheet.Root>
          </div>
          <div className={styles.trailing}>
            <Typography variant="body-m-bold-flat">{priceText}</Typography>
          </div>
        </div>
      </ListItem>
    </label>
  )
}

export { Room }
